import * as d3 from 'd3';
import { Kpi } from './Kpi';
import { IBulletChart } from './IBulletChart';
import { BulletChartOptions } from './BulletChartOptions';
import { HorizontalAxis } from '../pieces/axes/index';
import { SvgManager } from '../SvgManager';
import { chartSm, chartXs } from '../../constants';
import { Symbols } from '../../models/Symbols';
import { RoundingHelper, ILogger, LoggerFactory } from '../../helpers/helpers';

import { getGlobalMinMax, getRanges, light, dark } from './BulletChartHelper';

interface BulletChartDispatch extends d3.Dispatch {
    mouseenter: (elt, d) => void;
    mouseleave: (elt, d) => void;
}

export class BulletChart implements IBulletChart {
    private _headingHeight = 20;
    private _headingGroup: d3.Selection<any>;
    private _xAxis: HorizontalAxis;
    private _targetRect: d3.Selection<any>;
    private _markerRect: d3.Selection<any>;
    private _markerWidth = 2;
    private _rectGroup: d3.Selection<any>;
    private _margin = 20;
    private _kpi: Kpi;
    private _optionsSelection: d3.Selection<any>;
    private _options: BulletChartOptions;
    private _contextGroup: d3.Selection<any>;
    private _tendencyGroup: d3.Selection<any>;
    private _qualityGroup: d3.Selection<any>;
    private _group: d3.Selection<any>;

    private _boxWidth = 50;
    private _format = '2.2f';
    private _roundingHelper: RoundingHelper;
    private _list: Kpi[];
    private _tendencyScale;
    private _markerHeight = 5;
    private _markerMargin = 3;
    private _height: number;

    private _light = light;
    private _dark = dark;
    private _logger: ILogger;
    private _optionsCount: number;

    private _dispatch: BulletChartDispatch;
    private _chartGroup: d3.Selection<any>;
    private _axisGroup: d3.Selection<any>;

    constructor(private _d3: any, svgManager: SvgManager) {
        const container = svgManager.container();
        this.init(container, svgManager.getSize().width, svgManager.getSize().height);
        this._logger = LoggerFactory.getLogger('WideTech.Charting.bulletChart', false, 0);
        this._roundingHelper = new RoundingHelper();
        svgManager.addResizeFunction(this.resize());
    }

    private resize() {
        const self = this;
        return (width, height) => {
            self._height = height;
            const usableWidth = width - 2 * self._margin;
            const xs = usableWidth < chartXs || height <= 40;
            const sm = !xs && (usableWidth < chartSm || height <= 50);
            const translateX: number = 20; // = xs ? 10 : (sm ? 15 : 20);
            let translateY: number; // = xs ? 1 : (sm ? 3 : 5);
            let tendencyX = 40;
            if (xs) {
                self._headingHeight = 12;
                //translateX = 10;
                translateY = 1;
                self._tendencyScale = 0.5;
                self._markerMargin = 1;
                self._markerHeight = 3;
                this._boxWidth = 40;
                tendencyX = 30;
            } else if (sm) {
                self._headingHeight = 15;
                //translateX = 15;
                translateY = 3;
                self._tendencyScale = 0.6;
                self._markerMargin = 2;
                self._markerHeight = 4;
                this._boxWidth = 45;
                tendencyX = 35;

            } else {
                self._headingHeight = 20;
                //translateX = 20;
                translateY = 5;
                self._tendencyScale = 0.7;
                self._markerMargin = 3;
                self._markerHeight = 5;
                this._boxWidth = 50;
            }
            //self._markerHeight = self._headingHeight - 2 * self._markerMargin;
            //self._headingHeight = xs ? 12 : (sm ? 15 : 20);
            //self.
            self._group.classed({
                'sm': sm,
                'xs': xs
            })
                .attr({
                    'transform': `translate(${translateX},${translateY})`
                });
            self._xAxis.resize(usableWidth);
            self._axisGroup.attr({
                'transform': `translate(0,${self._headingHeight})`
            });
            self._chartGroup.attr({
                'transform': `translate(0,${self._headingHeight})`
            });

            self._tendencyGroup.select('.tendency')
                .attr({
                    'transform': `translate(${this._boxWidth - 5},-4) scale(${this._tendencyScale})`
                });
            self.updateRectangles(self._kpi);
            self.updateMarkers(self._kpi);
            self._optionsSelection.attr({
                'transform': `translate(${usableWidth},0)`
            });
            self._optionsSelection.selectAll('.option')
                .attr('transform', (d, i) => `translate(${-(this._optionsCount - i) * this._boxWidth},0)`);
            self._optionsSelection.select('.tendency')
                .select(('text'))
                .attr(('x'), tendencyX);
        };
    }

    private setXs() {
        //
    }

    private setSm() {
        //
    }

    private setDefault() {
        //
    }

    update(kpi: Kpi) {
        const range = getGlobalMinMax(kpi);
        const min = range.min;
        const max = range.max;
        this._xAxis.update(min, max);
        this.updateHeading(kpi);
        this.updateMarkers(kpi);
        this.updateRectangles(kpi);
        this.updateContext(kpi);
        this.updateQuality(kpi);
        this.updateTendency(kpi);
        this.updateColors(kpi);
        this._kpi = kpi;
    }

    options(options: BulletChartOptions) {
        this.updateOptionVisibility('context', options.isContextVisible);
        this.updateOptionVisibility('quality', options.isQualityVisible);
        this.updateOptionVisibility('tendency', options.isTendencyVisible);
        let count = 0;
        if (options.isContextVisible) {
            count = 1;
            if (options.isQualityVisible) {
                count++;
            }
            if (options.isTendencyVisible) {
                count++;
            }
            this.moveOption(count, this._contextGroup);
        }
        if (options.isQualityVisible) {
            count = 1;
            if (options.isTendencyVisible) {
                count++;
            }
            this.moveOption(count, this._qualityGroup);
        }


        if (options.isRangeVisible) {
            this._rectGroup.select('.range').style('fill', this._light);
        } else {
            this._rectGroup.select('.range').style('fill', this._dark);
        }

        if (options.isTargetVisible) {
            this._targetRect.style('visibility', 'visible');
        } else {
            this._targetRect.style('visibility', 'hidden');
        }

        this._format = `2.${options.decimals}f`;
        this.updateHeading(this._kpi);
        this._options = options;
        this._optionsCount = 3; //count;
    }

    list(value?: Kpi[]): Kpi[] {
        //TODO
        //if (arguments.length) {
        //    this._list = value;
        //    const selection = this._group
        //        .selectAll('.bullet-chart-individual')
        //        .data(this._list);
        //    this.append(selection);
        //}
        return this._list;
    }

    on(event: string, callback: (d, i) => void): void {
        this._dispatch.on(event, callback);
    }

    private append(selection: d3.selection.Update<Kpi>) {
        const group = selection
            .enter()
            .append('g')
            .classed('bullet-chart-individual', true)
            .attr('id', d => `bulletChart${d.name}`);
    }

    private updateSelection() {
        //
    }

    private delete() {
        //
    }

    private updateColors(kpi: Kpi) {
        if (kpi.coloringConditions && kpi.coloringConditions.length > 0) {
            const matchedConditions = kpi.coloringConditions.filter(cc => cc.condition(kpi));
            if (matchedConditions.length > 0) {
                const matchedCondition = matchedConditions[0];
                if (matchedCondition) {
                    this._rectGroup.selectAll('.background')
                        .style('fill', matchedCondition.colorBack ? matchedCondition.colorBack : this._dark);
                    this._xAxis.group()
                        .select('.domain')
                        .style({
                            'stroke': matchedCondition.colorAxis,
                            'stroke-width': '2px'
                        });
                    this._xAxis.group()
                        .selectAll('text')
                        .style({
                            'fill': matchedCondition.colorAxis
                        });
                }

            } else {
                this._rectGroup.selectAll('.background')
                    .style('fill', this._dark);
                this._xAxis.group()
                    .select('.domain')
                    .style({
                        'stroke': '',
                        'stroke-width': ''
                    });
                this._xAxis.group()
                    .selectAll('text')
                    .style({
                        'fill': ''
                    });
            }

        }
    }

    private updateHeading(kpi: Kpi) {
        const value = kpi.value;
        const isValueAvailable = value !== null && value !== undefined;
        let displayValue: string;
        if (this._options && this._options.valueFormat) {
            displayValue = this._options.valueFormat(kpi);
            if (displayValue === 'NaN') {
                displayValue = this._options.valueFormat(value);
            }
        } else {
            displayValue = d3.format(this._format)(value);
        }

        this._headingGroup.text(kpi.name + ': ' + (isValueAvailable ? displayValue : '-') + ' ' + kpi.unit);
    }


    private moveOption(count: number, group: d3.Selection<any>) {
        group.attr({
            'transform': `translate(${-count * this._boxWidth},0)`
        });
    }

    private updateMarkers(kpi: Kpi): void {
        const linearScale = this._xAxis.scale();
        try {

            const isValueAvailable = kpi.value !== null && kpi.value !== undefined;

            this._markerRect
                .style({
                    'fill': () => {
                        const color = kpi.color;
                        return color;
                    },
                    'visibility': isValueAvailable ? 'visible' : 'hidden'
                })
                .transition()
                .attr({
                    'width': linearScale(isNaN(kpi.value) ? 0 : kpi.value),
                    'height': this._markerHeight,
                    'y': ((this._headingHeight - this._markerHeight) / 2)
                });
            this._targetRect
                .attr({
                    'x': linearScale(isNaN(kpi.target) ? 0 : kpi.target),
                    'y': this._markerMargin,
                    'height': this._headingHeight - 2 * this._markerMargin
                });
        } catch (e) {
            this._logger.error(e);
        }
    }

    private updateTarget(kpi: Kpi) {
        if (kpi.target || kpi.target === 0) {
            this._targetRect
                .transition()
                .attr({
                    'x': this._xAxis.scale()(kpi.target) - this._markerWidth / 2,
                    'height': this._headingHeight - 4
                })
                .style('visibility', 'visible');
        } else {
            this._targetRect
                .style('visibility', 'hidden');

        }
    }

    private updateRectangles(kpi: Kpi): void {
        //TODO push color with range
        const ranges = getRanges(kpi);
        const linearScale = this._xAxis.scale();
        try {
            this._rectGroup.selectAll('rect')
                .data(ranges)
                .transition()
                .attr({
                    'x': d => {
                        const x = linearScale(d.min);
                        return !isNaN(x) ? x : 0;
                    },
                    'width': d => {
                        const width = linearScale(d.max) - linearScale(d.min);
                        return width > 0 ? width : 0;
                    },
                    'height': this._headingHeight
                });
        } catch (e) {
            this._logger.error(e);
        }
    }

    private updateContext(kpi: Kpi) {
        this._contextGroup.select('text')
            .text(kpi.context);
    }

    private updateQuality(kpi: Kpi) {
        this._qualityGroup.select('text')
            .text(kpi.quality);
    }

    private updateTendency(kpi: Kpi) {
        const isTendencyAvailable = kpi.variation !== null && kpi.variation !== undefined;
        this._tendencyGroup.select('text')
            .text(isTendencyAvailable ? this._d3.format('2.0%')(kpi.variation) : '-');
        this._tendencyGroup.select('path')
            .transition()
            .attr({
                'd': kpi.variation > 0
                    ? this._d3.svg.symbol().type(Symbols.triangleUp)
                    : this._d3.svg.symbol().type(Symbols.triangleDown)
            });

    }

    private updateOptionVisibility(classed: string, isVisible: boolean) {
        const selection = this._optionsSelection.select(`.${classed}`);
        if (isVisible) {
            selection
                .style({
                    'visibility': 'visible'
                });
        } else {
            selection.style({
                'visibility': 'hidden'
            });
        }
    }

    private init(container: d3.Selection<any>, width: number, height: number) {
        this._dispatch = (d3.dispatch('mouseenter', 'mouseleave') as BulletChartDispatch);
        const self = this;
        const glob = container.append('g');
        glob.attr({
            'transform': `translate(${this._margin},5)`
        })
            .style('pointer-events', 'all')
            .on('mouseenter', function () {
                self._dispatch.mouseenter(this, self._kpi);
            })
            .on('mouseleave', function () {
                self._dispatch.mouseleave(this, self._kpi);
            });
        this._group = glob;
        const usableWidth = width - 2 * this._margin;
        this.createHeading(glob, this._headingHeight);
        this.createChart(glob, usableWidth);
        this.createOptions(glob, usableWidth, this._headingHeight);
    }

    private createHeading(glob: d3.Selection<any>, height: number) {
        const textGroup = glob.append('g')
            .classed('heading', true)
            .attr({
                'transform': `translate(0,${height / 2})`
            });
        this._headingGroup = textGroup
            .append('text')
            .text('')
            .attr({
                'x': 0,
                'y': 0
            });
    }

    private createChart(glob: d3.Selection<any>, width: number) {
        const chart = glob.append('g')
            .classed('chart', true)
            .attr({
                'transform': `translate(${0}, ${this._headingHeight})`
            });
        this._chartGroup = chart;
        const axisGroup = chart.append('g')
            .classed('axis-group', true)
            .attr({
                'transform': `translate(0,${this._headingHeight})`
            });
        this._axisGroup = axisGroup;
        this._xAxis = new HorizontalAxis(width, 0, 1, 10, axisGroup);
        this.createRectangles(chart, width, this._headingHeight);
        this.createMarkers(chart, this._headingHeight);
    }

    private createRectangles(chart: d3.Selection<any>, width: number, height: number) {

        this._rectGroup = chart.insert('g', '.axis-group')
            .classed('rectangles', true);
        const linearScale = this._xAxis.scale();
        this.appendRect(this._rectGroup, linearScale, height, this._dark, 'background');
        this.appendRect(this._rectGroup, linearScale, height, this._light, 'range');
        this.appendRect(this._rectGroup, linearScale, height, this._dark, 'background');
    }

    private createMarkers(chart: d3.Selection<any>, height: number) {
        const kpiGroup = chart.append('g')
            .classed('kpi', true); //marker
        this._markerRect = kpiGroup.append('rect')
            .attr({
                'x': 0,
                'y': ((height - this._markerHeight) / 2),
                'height': this._markerHeight
            });

        //target
        this._targetRect = kpiGroup.append('rect')
            .classed('target', true)
            .attr({
                'y': this._markerMargin,
                'width': this._markerWidth,
                'height': height - 2 * this._markerMargin

            });
    }

    private appendRect(rectGroup: d3.Selection<any>,
        scale: d3.scale.Linear<any, any>,
        height: number,
        color: string,
        classed: string) {
        const rectHeight = height;
        rectGroup.append('rect')
            .classed(classed, true)
            .attr({
                //'x': scale(min),
                'y': height - rectHeight,
                //'width': scale(max) - scale(min),
                'height': rectHeight
            })
            .style({
                'fill': color
            });
    }

    private createOptions(glob: d3.Selection<any>, width: number, height: number) {
        this._optionsSelection = glob.append('g')
            .classed('options', true)
            .attr({
                'transform': `translate(${width},0)`
            });

        this.createContext(height);
        this.createQuality(height);
        this.createTendency(height);
    }

    private createContext(height: number) {
        this._contextGroup = this.createOption('context', this._boxWidth);

        this._contextGroup.append('g')
            .attr({
                'transform': `translate(0,${height / 2})`
            })
            .append('text')
            .attr({
                'text-anchor': 'middle'
            });
    }

    private createQuality(height: number) {
        this._qualityGroup = this.createOption('quality', this._boxWidth);

        this._qualityGroup
            .append('g')
            .attr({
                'transform': `translate(0,${height / 2})`
            })
            .append('text')
            .attr({
                'text-anchor': 'middle'
            });
    }

    private createTendency(height: number) {
        const boxWidth = this._boxWidth;
        this._tendencyGroup = this.createOption('tendency', boxWidth);
        this._tendencyGroup
            .attr({
                'transform': `translate(-${1 * boxWidth},0)`
            });
        const tgcGroup = this._tendencyGroup
            .append('g')
            .attr({
                'transform': `translate(0,${height / 2})`
            });
        tgcGroup.append('path')
            .classed('tendency', true)
            .attr({
                'transform': `translate(${boxWidth - 5},-4) scale(0.7,0.7)`
            });
        tgcGroup.append('text')
            .attr({
                'x': boxWidth - 10
            })
            .style({
                'text-anchor': 'end'
            });
    }

    private createOption(classed: string, boxWidth: number): d3.Selection<any> {
        const group = this._optionsSelection.append('g')
            .classed(classed, true)
            .classed('option', true);
        return group;
    }

}